/* global RequestInit */
/* global RequestInfo */

import { v4 as uuidv4 } from "uuid"
import {
  extractErrorMessageErrorCodeAndTranslationKey,
  fallbackError,
} from "../util/error-handler"
import HeapsError from "../util/heaps-error"
import { getCachedUser, removeCachedUser } from "./cached-user"

const HEAPS_BACKEND_URL =
  process.env.GATSBY_HEAPS_BACKEND_URL || "https://drinks-staging.heapsapp.com"
const API_KEY =
  process.env.GATSBY_HEAPS_API_KEY || "f02bed37-f90f-4b60-87d9-b0d93a248f46"
const API_VERSION = process.env.GATSBY_HEAPS_API_VERSION || "20191101"
const APP_ID = process.env.GATSBY_HEAPS_APP_ID || "unknown_default"

const RELOAD_ON_401_KEY = "hg::last-401-reload"

const defaultHeaders = {
  "X-Drinks-Api-Key": API_KEY,
  "X-Drinks-Version": API_VERSION,
  "X-Drinks-App-Id": APP_ID,
  "Content-Type": "application/json",
}

const getHeaders = () => {
  if (
    typeof window === "undefined" ||
    !localStorage ||
    typeof localStorage === "undefined"
  ) {
    return defaultHeaders
  }

  const headers: any = { ...defaultHeaders }
  const user = getCachedUser()
  if (user?.access_token) {
    headers["Authorization"] = `Bearer ${user?.access_token}`
  }
  headers["X-Drinks-Idempotency-Key"] = uuidv4()

  return headers
}

const fetchWithBase = (
  url: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  return fetch(HEAPS_BACKEND_URL + url.toString(), init)
}

type FetchHookOptions = {
  anonymous: boolean
}

type FetchRequestOptions = {
  data?: object
  retries?: number
  headers?: any
  followRedirect?: boolean
}

export const useFetchRequest = ({ anonymous }: FetchHookOptions) => {
  const fetchRequest = async <T = any>(
    method: string,
    url: string,
    options?: FetchRequestOptions
  ): Promise<T> => {
    const data = options?.data
    const retries = options?.retries || 0
    const headers = options?.headers
    const followRedirect = options?.followRedirect || false

    const config: RequestInit = {
      method,
      headers: {
        ...(anonymous ? defaultHeaders : getHeaders()),
        ...headers,
      },
      redirect: followRedirect ? "follow" : "manual",
    }

    if (data) {
      config.body = JSON.stringify(data)
    }

    try {
      const fetchResponse = await fetchWithBase(url, config)

      if (!fetchResponse.ok) {
        // This block of code catches the requests that are cancelled due to followRedirect === false
        if (fetchResponse.type === "opaqueredirect") {
          // There was a request that was redirected. Instead of following redirect, we do the refetch ourselves. Returning data will trigger react-query onSuccss
          return new Promise((resolve) => {
            resolve({ data: {} } as any)
          })
        }

        // Retry if status code is 409. Max 2 retries.
        else if (fetchResponse.status === 409 && retries < 2) {
          return await fetchRequest(method, url, {
            data: data,
            retries: retries + 1,
          })
        }

        // Unauthorized. Probably token expired
        else if (fetchResponse.status === 401 && !anonymous) {
          // TODO: implement refresh token logic
          removeCachedUser()
          localStorage?.removeItem("hg::cache::cart")
          localStorage?.removeItem("hg::cache::order")

          // Reload in place instead of redirecting to "/", so a direct link
          // (e.g. to a restaurant) is not lost. After the cache clearing
          // above, the reloaded page starts a fresh guest session (HEA-3173).
          // If we already reloaded within the last 10 seconds, 401s persist
          // even for a fresh session - redirect to "/" instead of looping.
          const lastReloadAt = Number(
            sessionStorage?.getItem(RELOAD_ON_401_KEY) || 0
          )
          if (Date.now() - lastReloadAt > 10000) {
            sessionStorage?.setItem(RELOAD_ON_401_KEY, Date.now().toString())
            window.location.reload()
          } else {
            window.location.href = "/"
          }

          return new Promise((resolve) => {
            resolve({ data: {} } as any)
          })
        }

        // Regular API error
        else {
          const error = await fetchResponse.json()
          const { errorMessage, translationKey, errorCode } =
            extractErrorMessageErrorCodeAndTranslationKey(error)
          // propagate error so that it can be handled appropriately on different places in the app with errorMessage
          // Access error in functions with getErrorMessage util function in /error-handler.ts
          throw new HeapsError({
            errorMessage,
            translationKey,
            errorCode,
            statusCode: fetchResponse.status,
          })
        }
      }

      const responseContentType = fetchResponse.headers.get("content-type")
      const responseHasBody =
        responseContentType?.startsWith("application/json")

      // API returns 200 on POST but no response body
      // When fetch is trying to deserialize empty response body, it results in error
      // We want to continue with success flow
      if (!responseHasBody) {
        return new Promise((resolve) => {
          resolve({ data: {} } as any)
        })
      }

      return fetchResponse.json()
    } catch (error: any) {
      // If it's the error we threw in try {} block, just propagate
      if (error instanceof HeapsError) {
        throw error
      }

      // Fetch API call results in error in two cases: network error or cors error
      // So this should be one of the two. Throw generic error
      const { errorMessage, translationKey, errorCode } = fallbackError

      // Propagate error so that it can be handled appropriately on different places in the app with errorMessage
      // Access error in functions with getErrorMessage util function in /error-handler.ts
      // Try sending error.message since it's probably more relevant and better hint
      throw new HeapsError({
        errorMessage: error?.message || errorMessage,
        translationKey,
        errorCode,
      })
    }
  }

  return fetchRequest
}
